import { notesGridSx } from '../../components/entity-notes/notesColumn';
import { alpha, type Theme } from '@mui/material';
import { parseISO, getDay } from 'date-fns';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import { COUNTRIES_MAP } from '../../utils/countries';
import { ALL_GROUP_OPTIONS } from '../../constants/tracking-groupings';

/**
 * A single active report filter. The entity-filter box and the right-click "Report"
 * menu write into one unified list — several entries on a field become an IN.
 */
export type FilterEntry = { field: string; value: string; label: string };

export function fmtDate(d: Date): string {
  // Use local date parts to avoid timezone shift (toISOString converts to UTC)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function fmtNum(n: number): string {
  return Number(n).toLocaleString();
}

export function fmtMoney(n: number): string {
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtPct(n: number): string {
  return `${Number(n).toFixed(1)}%`;
}

export function fmtPct2(n: number): string {
  return `${Number(n).toFixed(2)}%`;
}

/** ROAS: revenue/cost. 1.00 is breakeven. */
export function fmtRoas(n: number): string {
  return Number(n).toFixed(2);
}

/** Display order + labels for the lead attributes carried on each postback. */
export const LEAD_FIELDS: { key: string; label: string }[] = [
  { key: 'age', label: 'Age' },
  { key: 'homeOwnership', label: 'Home ownership' },
  { key: 'insured', label: 'Currently insured' },
  { key: 'currentInsuranceCompany', label: 'Insurance company' },
  { key: 'multiDriver', label: 'Multi-driver' },
  { key: 'multiVehicle', label: 'Multi-vehicle' },
];

export function labelForField(field: string): string {
  return ALL_GROUP_OPTIONS.find(g => g.value === field)?.label || field;
}

/** True if a date group_key (YYYY-MM-DD) falls on a Sunday. */
export function isSundayKey(value: unknown): boolean {
  if (!value) return false;
  const d = parseISO(String(value));
  return !isNaN(d.getTime()) && getDay(d) === 0;
}

/** Grouping fields that map to an editable entity, keyed to its useModalState name. */
export const ENTITY_MODAL_NAMES: Record<string, string> = {
  offer_id: 'offer', network_id: 'network', traffic_source_id: 'traffic-source',
  flow_id: 'flow', landing_page_id: 'landing-page',
};

export const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/** Render a group_key for display: hour → HH:00, country ISO → name, empty → (none). */
export function formatDimValue(groupBy: string, value: string | number | null | undefined): string {
  if (groupBy === 'hour' && value !== null && value !== undefined && value !== '') {
    return `${String(value).padStart(2, '0')}:00`;
  }
  // Country is stored as an ISO-3166 alpha-2 code — show the full name (fall back to the code).
  if (groupBy === 'country' && value) {
    return COUNTRIES_MAP[String(value).toUpperCase()] || String(value);
  }
  if (value === '' || value === null || value === undefined) return '(none)';
  return String(value);
}

export const GROUP_OPTION_VALUES = ALL_GROUP_OPTIONS.map(g => g.value);

/** Levels below the grouping; each nests a grid inside the last. */
export const MAX_DRILLDOWN_LEVELS = 3;

// A level: one dimension, or up to MAX_DIMS_PER_LEVEL joined by '+'.
export { MAX_DIMS_PER_LEVEL } from '../../constants/tracking-groupings';
export const levelDims = (level: string | undefined): string[] => (level ? level.split('+').filter(Boolean) : []);

// Emptying a level drops it and those below — they have nothing to hang from.
export const setLevelAt = (chain: string[], i: number, next: string): string[] =>
  (next ? [...chain.slice(0, i), next, ...chain.slice(i + 1)] : chain.slice(0, i));
export const usedElsewhere = (groupBy: string, chain: string[], i: number): string[] =>
  [groupBy, ...chain.filter((_, j) => j !== i)].flatMap(levelDims);
export const levelOf = (dims: string[]): string => dims.join('+');

export const dimField = (dim: string): string => `_dim_${dim}`;
export const apiSortField = (field: string | undefined): string | undefined => {
  if (field === 'display_name') return 'name';
  return field?.startsWith('_dim_') ? field.slice('_dim_'.length) : field;
};

// The grid drops a sort on a column it doesn't have, re-sorting under any open drilldown.
export function sortForLevel(model: GridSortModel, level: string): GridSortModel {
  const dims = levelDims(level);
  const own = new Set(dims.map(dimField));
  return model.map((s) => {
    const nameLike = s.field === 'display_name' || s.field.startsWith('_dim_');
    if (!nameLike) return s;
    if (dims.length > 1) return own.has(s.field) ? s : { ...s, field: dimField(dims[0]) };
    return s.field === 'display_name' ? s : { ...s, field: 'display_name' };
  });
}

export const gridSx = {
  ...notesGridSx,
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
  '& .MuiDataGrid-pinnedRows': {
    bgcolor: 'action.hover',
    fontWeight: 700,
    '& .MuiDataGrid-cell': { fontWeight: 700 },
  },
  '& .MuiDataGrid-row:nth-of-type(even)': {
    bgcolor: 'action.hover',
  },
  // Sunday rows (grouping by date): tint + a bold left accent so week boundaries pop.
  // Applied at the CELL level so it paints over the zebra stripe above (the row-level
  // even-row rule would otherwise win by specificity and hide it).
  '& .sunday-row .MuiDataGrid-cell': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.13),
  },
  '& .sunday-row:hover .MuiDataGrid-cell': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.30 : 0.18),
  },
  // The left accent goes on the ROW. The cell-level equivalent never matches — MUI's
  // first cell is not :first-of-type inside the row — and a row-level *background*
  // would lose to the cell rules above, so the two properties sit at different levels.
  '& .sunday-row': {
    boxShadow: (theme: Theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
  },
  '& .MuiDataGrid-detailPanel': { bgcolor: 'background.default' },
  '& .profit-positive': { color: 'success.main', fontWeight: 600 },
  '& .profit-negative': { color: 'error.main', fontWeight: 600 },
  '& .MuiDataGrid-cell': {
    display: 'flex',
    alignItems: 'center',
  },
} as const;
